import type { Appointment } from "@medplum/fhirtypes";
import { AD_HOC_PROTOCOL_TITLE, isOrderBookable } from "@repo/domain";
import type {
  ActivityDefinitionRow,
  AppointmentRow,
  CarePlanRow,
  EpisodeOfCareRow,
  PlanDefinitionRow,
  ProcedureRow,
} from "../fhir/types";
import { isProcedureNotPerformed, isProcedureResolved } from "../lib/procedure-lifecycle";
import type { OrderBookingStatus, OrderSetBookingDto } from "./order-set-booking";
import { type OrderSetProjectionRow, projectOrderSet } from "./order-set-projection";
import {
  bindingOrderSetAppointment,
  deriveRequestSchedulingStates,
  rollupGroupSchedulingStatus,
} from "./scheduling-state";
import { protocolDisplayTitle } from "./shared/protocol-display-title";

export interface BookingProjectionRow {
  carePlan?: CarePlanRow;
  cases: EpisodeOfCareRow[];
  anchor: OrderSetProjectionRow["anchor"];
  orders: Array<OrderSetProjectionRow["orders"][number] & { definition?: ActivityDefinitionRow }>;
  protocolDefinition?: PlanDefinitionRow;
  appointments: AppointmentRow[];
  procedures: ProcedureRow[];
}

export function projectBookingRow(row: BookingProjectionRow): OrderSetBookingDto {
  const set = projectOrderSet(row);
  const caseModel = row.cases.find((candidate) =>
    row.anchor.carePlanId ? candidate.domainId === row.carePlan?.episodeOfCareId : candidate.status === "active",
  );
  const ready = Boolean(caseModel?.careManager?.reference || caseModel?.careManager?.identifier?.value);
  const storedByOrderId = new Map(
    row.orders.flatMap((order) => (order.orderId ? [[order.orderId, order] as const] : [])),
  );
  // Both outcomes off the same join, in one pass: the Procedure is the record
  // that the order was carried out or deliberately skipped.
  const performed = new Set<string>();
  const notPerformed = new Set<string>();
  // The patient's procedures, narrowed to this set: a member id is unique only inside its set.
  for (const procedure of row.procedures.filter((candidate) => candidate.orderSetJoinKey === set.orderSetId)) {
    const target =
      isProcedureResolved(procedure.status) && !isProcedureNotPerformed(procedure.status)
        ? performed
        : isProcedureNotPerformed(procedure.status)
          ? notPerformed
          : undefined;
    if (target && procedure.orderId) target.add(procedure.orderId);
  }
  const orderIds = row.orders.flatMap((order) => (order.orderId ? [order.orderId] : []));
  const scheduling = deriveRequestSchedulingStates(
    orderIds.map((orderId) => ({
      orderId,
      performed: performed.has(orderId),
      notPerformed: notPerformed.has(orderId),
    })),
    row.appointments as Appointment[],
    set.orderSetId,
  );
  const steps = set.steps.map((step) => ({
    stepId: step.stepId,
    orders: step.orders
      .filter((order) => order.status !== "revoked" || notPerformed.has(order.orderId))
      .map((order) => {
        const state = scheduling.get(order.orderId);
        const status: OrderBookingStatus = state?.schedulingStatus ?? "unscheduled";
        return {
          orderId: order.orderId,
          title:
            storedByOrderId.get(order.orderId)?.definition?.title ??
            storedByOrderId.get(order.orderId)?.definition?.name ??
            order.definition.url,
          definition: { url: order.definition.url, version: order.definition.version },
          scheduling: {
            status,
            ...(state?.visitId ? { visitId: state.visitId } : {}),
            ...(state?.scheduledStart ? { scheduledStart: state.scheduledStart } : {}),
            isBookable: ready && isOrderBookable(set, order, status !== "unscheduled"),
          },
        };
      }),
  }));
  const active = steps.flatMap((step) => step.orders);
  const examOnly = set.steps.length === 0;
  const binding = bindingOrderSetAppointment(row.appointments, set.orderSetId);
  return {
    orderSetId: set.orderSetId,
    patientId: set.patientId,
    ...(caseModel?.domainId ? { caseId: caseModel.domainId } : {}),
    durationMinutes: set.durationMinutes,
    isBookable:
      ready && (examOnly ? set.status === "active" && !binding : active.some((order) => order.scheduling.isBookable)),
    protocolTitle: row.anchor.protocolUrl ? protocolDisplayTitle(row.protocolDefinition) : AD_HOC_PROTOCOL_TITLE,
    status:
      active.length === 0 && binding
        ? "scheduled"
        : rollupGroupSchedulingStatus(active.map((order) => order.scheduling.status)),
    steps,
    bookableOrderIds: active.filter((order) => order.scheduling.isBookable).map((order) => order.orderId),
  };
}
